// Hoja de seguridad (SDS) de un producto en el bucket safety-sheets (Fase 0, bloque 10).
import { nombreSeguroStorage } from './nombre-storage';
import { rutaEnBucket } from './ruta-bucket';

/**
 * Ruta del PDF: la que guarda sds_url (URL pública vieja o ruta) y, si no se
 * puede leer, la que usa la subida (Products.tsx): `<empresa>/<product_id>.pdf`.
 */
export function rutaSds(companyId: string, productId: string, sdsUrl: string | null | undefined): string {
  return rutaEnBucket('safety-sheets', sdsUrl) ?? `${companyId}/${nombreSeguroStorage(`${productId}.pdf`)}`;
}

// Lo mismo que M26 pone en el bucket (file_size_limit y allowed_mime_types).
export const SDS_MAX_MB = 10;
export const SDS_TIPO = 'application/pdf';
export const MENSAJE_SDS_NO_PDF = 'La hoja de seguridad debe ser un archivo PDF.';

/** Mensaje para el usuario o null si el archivo se puede subir como SDS. */
export function validarSds(archivo: { name: string; type: string; size: number }): string | null {
  const esPdf = /\.pdf$/i.test(archivo.name) || archivo.type === SDS_TIPO;
  if (!esPdf) return MENSAJE_SDS_NO_PDF;
  if (archivo.size > SDS_MAX_MB * 1024 * 1024) return `El archivo supera el tamaño máximo permitido (${SDS_MAX_MB} MB).`;
  return null;
}

export const MENSAJE_SDS_PDF_INVALIDO = 'El archivo no es un PDF válido.';

/**
 * ¿El archivo empieza con la cabecera "%PDF" (PE-A3c)? El nombre y el tipo no bastan: un .docx renombrado a
 * .pdf pasa validarSds. Se leen solo los primeros 4 bytes, con FileReader (también existe en las pruebas).
 */
export function tieneCabeceraPdf(archivo: Blob): Promise<boolean> {
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => {
      const b = new Uint8Array(lector.result as ArrayBuffer);
      resolve(b.length === 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46); // "%PDF"
    };
    lector.onerror = () => resolve(false);
    lector.readAsArrayBuffer(archivo.slice(0, 4));
  });
}

/**
 * El archivo que se pasa a upload(). Con un File, storage-js no usa la opción
 * contentType: el bucket ve el tipo que puso el navegador (a veces vacío o
 * application/x-pdf), y con allowed_mime_types (M26) lo rechazaría. Se envuelve
 * en un File con application/pdf; no copia los datos.
 */
export function sdsParaSubir(archivo: File): File {
  if (archivo.type === SDS_TIPO) return archivo;
  return new File([archivo], archivo.name, { type: SDS_TIPO, lastModified: archivo.lastModified });
}

/**
 * ¿Se puede borrar la fila del producto después de remove()?
 * remove() no da error si el archivo no existe o si no hay permiso: devuelve
 * una lista vacía. Por eso, con lista vacía se mira si el archivo sigue ahí.
 */
export function decisionBorradoSds(
  rmErr: unknown,
  removed: unknown[] | null | undefined,
  existeAun: boolean,
): 'borrar' | 'conservar' {
  if (rmErr) return 'conservar';
  if (removed && removed.length > 0) return 'borrar';
  return existeAun ? 'conservar' : 'borrar';
}
